/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  // IconButton,
  Popover,
  Typography,
} from "@mui/material";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
// import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
// import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import useTailwindBrand from "../Theme/useTailwindBrand";
import Teachr from "../Objects/models/Teachr";
import {
  BadgeStats,
  getBadgeLevelFromStats,
} from "../config/badgeLevelsConfig";
import { getCurrentLevel, LEVEL_CONFIG } from "../Hooks/Dashboard/useHome";
import ProfileImage from "./AppBar/ProfileImage";

export interface PhotoSectionDisplayProps {
  profileImageUrl?: string | null;
  firstName?: string;
  lastName?: string;
  age?: string | null;
  email?: string | null;
  role?: string;
  memberSinceYear?: string | null;
  teachr?: Teachr;
  image?: string | null;
  profileLoading?: boolean;
  /** Stats pour le calcul du niveau de badge */
  badgeStats?: BadgeStats;
}

export interface PhotoSectionActionsProps {
  isEditing?: boolean;
  onImageUpload: (value: File | null, error: boolean) => void;
  loading?: boolean;
  errorMessage?: string | null;
  disableGridItem?: boolean;
}

export type PhotoSectionProps = PhotoSectionDisplayProps &
  PhotoSectionActionsProps;

const formatEuro = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(0)}k€` : `${n}€`;

const PhotoSection: React.FC<PhotoSectionProps> = ({
  // profileImageUrl, --- IGNORE ---
  firstName,
  lastName,
  age,
  email,
  role,
  memberSinceYear = null,
  isEditing = false,
  onImageUpload,
  loading = false,
  errorMessage = null,
  teachr,
  // image, --- IGNORE ---
  profileLoading = false,
  disableGridItem = false,
  badgeStats,
}) => {
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  // const imageSrc = image ?? profileImageUrl ?? undefined;
  const isLoading = profileLoading ?? loading;
  const { brandConfig } = useTailwindBrand();
  const displayName = [firstName, lastName].filter(Boolean).join(" ") || "";
  const primaryColor = brandConfig.colors?.primary ?? "#1976d2";
  const currentLevelNum = getCurrentLevel(teachr)
  const currentLevelInfo = LEVEL_CONFIG[currentLevelNum] ?? LEVEL_CONFIG[1];

  const currentLevel = useMemo(
    () => getBadgeLevelFromStats(badgeStats ?? {}),
    [badgeStats]
  );

  const card = (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        // borderRadius: 3,
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: "center",
            gap: { xs: 3, md: 4 },
            maxWidth: 720,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ position: "relative" }}>
              <ProfileImage
                isAvatar={false}
                teachr={teachr}
                // image={imageSrc}
                getFile={onImageUpload}
                width={160}
                height={160}
                profileLoading={isLoading}
                btnDownload={false}
                isEditing={isEditing}
              />
            </Box>
            {memberSinceYear && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: brandConfig.fontFamily,
                  fontWeight: 600,
                  color: "text.secondary",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: "grey.100",
                }}
              >
                Membre depuis {memberSinceYear}
              </Typography>
            )}
            {errorMessage && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  fontFamily: brandConfig.fontFamily,
                  fontWeight: 500,
                  textAlign: "center",
                  maxWidth: 200,
                }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>

          {/* Droite : nom, prénom, âge, email + badge avec icône info */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              textAlign: { xs: "center", md: "left" },
              gap: 2,
            }}
          >
            {displayName && (
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontFamily: brandConfig.fontFamily,
                  color: primaryColor,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
            )}
            {role && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: brandConfig.fontFamily,
                  color: "text.secondary",
                  mt: -1,
                }}
              >
                {role}
              </Typography>
            )}

            {/* Âge + Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                gap: 1.5,
                width: "100%",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {age && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 1.25,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "grey.200",
                    minWidth: { xs: "100%", sm: "auto" },
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "grey.100",
                      borderColor: "grey.300",
                    },
                  }}
                >
                  <CakeOutlinedIcon
                    sx={{ color: "text.secondary", fontSize: 20, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: brandConfig.fontFamily,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    {age}
                  </Typography>
                </Box>
              )}
              {email && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 1.25,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "grey.200",
                    minWidth: { xs: "100%", sm: "auto" },
                    maxWidth: 320,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "grey.100",
                      borderColor: "grey.300",
                    },
                  }}
                >
                  <EmailOutlinedIcon
                    sx={{ color: "text.secondary", fontSize: 20, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: brandConfig.fontFamily,
                      fontWeight: 500,
                      color: "text.primary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Badge + icône info */}
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: `${primaryColor}12`,
                  border: "1px solid",
                  borderColor: `${primaryColor}40`,
                }}
              >
                <EmojiEventsOutlinedIcon
                  sx={{ fontSize: 28, color: primaryColor }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: brandConfig.fontFamily,
                    fontWeight: 700,
                    color: primaryColor,
                  }}
                >
                  {`Niveau ${teachr?.level ?? 1} : ${currentLevelInfo?.name} ${currentLevelInfo?.emoji}`}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => setInfoAnchor(e.currentTarget)}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: primaryColor, bgcolor: `${primaryColor}12` },
                }}
                aria-label="Infos sur mon niveau"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Box> */}
          </Box>
        </Box>
      </CardContent>

      {/* Popover : infos du niveau actuel */}
      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            p: 2.5,
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: brandConfig.fontFamily,
            fontWeight: 700,
            color: primaryColor,
            mb: 1.5,
          }}
        >
          Votre niveau : {currentLevelInfo?.name} {currentLevelInfo?.emoji}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: brandConfig.fontFamily,
            color: "text.secondary",
            display: "block",
            mb: 1,
          }}
        >
          Critères de ce niveau :
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, typography: "body2" }}>
          <li>Nombre d&apos;élèves : {currentLevel.minStudents}</li>
          <li>Heures déclarées : {currentLevel.minDeclaredHours}</li>
          <li>Mois de déclaration d&apos;affilée : {currentLevel.minConsecutiveMonths}</li>
          <li>Volume de cours généré : {formatEuro(currentLevel.minCourseVolume)}</li>
          <li>Profs parrainés : {currentLevel.minSponsoredTeachers}</li>
          <li>VA générée par les filleuls : {formatEuro(currentLevel.minVaFromSponsored)}</li>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontFamily: brandConfig.fontFamily,
            color: "text.secondary",
            display: "block",
            mt: 1.5,
            mb: 0.5,
          }}
        >
          Récompense obtenue :
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: brandConfig.fontFamily,
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {currentLevel.reward}
        </Typography>
      </Popover>
    </Card>
  );

  if (disableGridItem) return card;
  return <Grid item xs={12}>{card}</Grid>;
};

export default PhotoSection;
